import React, { useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import {
  FaTimes as X,
  FaCheck as Check,
  FaSpinner as Spinner,
} from 'react-icons/fa'
import {
  useCheckoutSession,
  type BillingCycle,
  type BillingProduct,
} from '@/hooks/useBilling'
import { useAuth } from '@/hooks/useAuth'
import { usePlatform } from '@/hooks/usePlatform'

interface UpgradeDialogProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * A single product card in the upgrade dialog. Knows about
 * billing cadence, whether it's the user's current plan (and
 * therefore disabled), and — for Studio Team only — the seat
 * count spinner.
 *
 * Pricing is expressed as monthly + annual per *unit*; the
 * Studio Team price is per-seat-per-month, so the visible
 * total is monthly/annual price × seats.
 */
interface PlanCardProps {
  product: BillingProduct
  name: string
  tagline: string
  monthlyPrice: number
  annualPrice: number
  /** Displayed beneath the price, e.g. "/ month" or "/ seat / month". */
  cadenceSuffix: string
  billingCycle: BillingCycle
  bullets: string[]
  featured?: boolean
  isCurrent: boolean
  onCheckout: (product: BillingProduct) => void
  isPending: boolean
  pendingProduct: BillingProduct | null
  seats?: number
  onSeatsChange?: (seats: number) => void
}

function PlanCard({
  product,
  name,
  tagline,
  monthlyPrice,
  annualPrice,
  cadenceSuffix,
  billingCycle,
  bullets,
  featured,
  isCurrent,
  onCheckout,
  isPending,
  pendingProduct,
  seats,
  onSeatsChange,
}: PlanCardProps) {
  const unitPrice = billingCycle === 'annual' ? annualPrice : monthlyPrice
  const multiplier = seats ?? 1
  const totalPrice = unitPrice * multiplier
  const thisPending = isPending && pendingProduct === product

  return (
    <div
      className={`relative flex flex-col rounded-xl border p-5 transition-colors duration-150 ${
        featured
          ? 'border-accent bg-accent-muted/30 shadow-medium'
          : 'border-brd-subtle'
      }`}
    >
      {featured && (
        <div className="absolute -top-2.5 right-4 px-2 py-0.5 rounded-full bg-accent text-white text-[10px] font-semibold uppercase tracking-wider">
          Most popular
        </div>
      )}
      <div className="text-sm font-semibold text-content-primary">{name}</div>
      <div className="text-xs text-content-tertiary mt-0.5">{tagline}</div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-2xl font-bold text-content-primary">
          ${totalPrice}
        </span>
        <span className="text-xs text-content-tertiary">{cadenceSuffix}</span>
      </div>
      {seats !== undefined && onSeatsChange && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-content-secondary">
          <label htmlFor={`seats-${product}`}>Seats</label>
          <div className="inline-flex items-center rounded-md border border-brd-subtle">
            <button
              type="button"
              onClick={() => onSeatsChange(Math.max(3, seats - 1))}
              disabled={seats <= 3}
              className="px-2 py-1 text-content-secondary hover:text-content-primary disabled:opacity-40"
              aria-label="Decrease seats"
            >
              −
            </button>
            <input
              id={`seats-${product}`}
              type="number"
              min={3}
              max={100}
              value={seats}
              onChange={e => {
                const v = Number(e.target.value)
                if (!Number.isFinite(v)) return
                onSeatsChange(Math.min(100, Math.max(3, Math.floor(v))))
              }}
              className="w-12 text-center bg-transparent text-content-primary text-xs font-mono focus:outline-none"
            />
            <button
              type="button"
              onClick={() => onSeatsChange(Math.min(100, seats + 1))}
              disabled={seats >= 100}
              className="px-2 py-1 text-content-secondary hover:text-content-primary disabled:opacity-40"
              aria-label="Increase seats"
            >
              +
            </button>
          </div>
          <span className="text-content-tertiary">(min 3)</span>
        </div>
      )}
      <ul className="mt-4 flex-1 space-y-1.5">
        {bullets.map(b => (
          <li
            key={b}
            className="flex items-start gap-1.5 text-[11px] text-content-secondary"
          >
            <Check className="w-3 h-3 mt-0.5 flex-shrink-0 text-success" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={() => onCheckout(product)}
        disabled={isCurrent || isPending}
        className={`mt-5 w-full px-3 py-2 rounded-lg text-sm font-semibold transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 ${
          featured
            ? 'bg-accent text-white hover:bg-accent-hover'
            : 'bg-surface-overlay text-content-primary hover:bg-surface-overlay/80'
        }`}
      >
        {thisPending && <Spinner className="w-3 h-3 animate-spin" />}
        {isCurrent
          ? 'Current plan'
          : thisPending
            ? 'Redirecting…'
            : `Choose ${name.replace('Busswerks ', '')}`}
      </button>
    </div>
  )
}

export const UpgradeDialog: React.FC<UpgradeDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { user } = useAuth()
  const platform = usePlatform()
  const checkout = useCheckoutSession()
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly')
  const [seats, setSeats] = useState<number>(3)
  const [error, setError] = useState<string | null>(null)
  const [pendingProduct, setPendingProduct] = useState<BillingProduct | null>(
    null,
  )

  // `user.tier` is the backend tier (`free | pro | studio`).
  // We don't yet persist which *bundle* produced that tier, so
  // we highlight the closest match: Studio Team for `studio`
  // users and Pro Platform for `pro` (a slight bias — Home
  // Platform subscribers will see Pro Platform marked current
  // rather than Home Platform, which is fine for disabling the
  // upgrade button but not precise for display).
  const backendTier = user?.tier ?? 'free'
  const isCurrent = (product: BillingProduct): boolean => {
    if (backendTier === 'studio') return product === 'studio-team'
    if (backendTier === 'pro') return product === 'pro-platform'
    return false
  }

  const handleCheckout = async (product: BillingProduct) => {
    setError(null)
    setPendingProduct(product)
    try {
      const { url } = await checkout.mutateAsync({
        product,
        billingCycle,
        seats: product === 'studio-team' ? seats : undefined,
      })
      window.location.href = url
    } catch (err) {
      setPendingProduct(null)
      setError(err instanceof Error ? err.message : 'Failed to start checkout')
    }
  }

  const cadenceSuffix = billingCycle === 'annual' ? '/ year' : '/ month'
  const seatSuffix = billingCycle === 'annual' ? '/ year' : '/ month'

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open) {
          setError(null)
          setPendingProduct(null)
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl max-h-[92vh] bg-surface-raised rounded-xl shadow-high z-50 flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-brd-subtle">
            <div>
              <Dialog.Title className="text-lg font-semibold text-content-primary">
                Choose your plan
              </Dialog.Title>
              <Dialog.Description className="text-xs text-content-tertiary mt-0.5">
                All three apps bundled — Layouts, MIDI Router, Sequencer — plus
                multiplayer. Pick the use context that fits.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button className="text-content-secondary hover:text-content-primary transition-colors duration-150">
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {platform.billingDisabled ? (
              <div className="py-6 text-center space-y-3">
                <p className="text-sm text-content-secondary">
                  Subscription plans are managed on the web.
                </p>
                <p className="text-xs text-content-tertiary leading-relaxed">
                  To upgrade, visit{' '}
                  <span className="font-semibold text-content-primary">
                    busswerks.io
                  </span>{' '}
                  in any browser and sign in with the same account. Once
                  upgraded, your new features will unlock here automatically on
                  next launch.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-2 px-4 py-2 rounded-lg bg-surface-overlay text-content-primary text-sm font-semibold"
                >
                  Got it
                </button>
              </div>
            ) : (
              <>
                <div className="flex justify-center">
                  <div className="inline-flex rounded-lg border border-brd-subtle p-1">
                    {(['monthly', 'annual'] as const).map(cycle => (
                      <button
                        key={cycle}
                        type="button"
                        onClick={() => setBillingCycle(cycle)}
                        className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-colors duration-150 ${
                          billingCycle === cycle
                            ? 'bg-accent text-white'
                            : 'text-content-secondary hover:bg-accent-muted'
                        }`}
                      >
                        {cycle === 'monthly' ? 'Monthly' : 'Annual'}
                        {cycle === 'annual' && (
                          <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider text-success">
                            -17%
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <PlanCard
                    product="home-platform"
                    name="Busswerks Home"
                    tagline="For personal use — hobbyists and home studios"
                    monthlyPrice={12}
                    annualPrice={120}
                    cadenceSuffix={cadenceSuffix}
                    billingCycle={billingCycle}
                    bullets={[
                      'All 3 apps: Layouts, MIDI, Sequencer',
                      'Unlimited layouts + MIDI routes',
                      'Every sequencer kit + FX bank',
                      'Project + sample cloud sync',
                      'AI routing suggestions',
                      'Share with up to 25 collaborators',
                    ]}
                    isCurrent={isCurrent('home-platform')}
                    onCheckout={handleCheckout}
                    isPending={checkout.isPending}
                    pendingProduct={pendingProduct}
                  />
                  <PlanCard
                    product="pro-platform"
                    name="Busswerks Pro"
                    tagline="For commercial use — engineers, venues, and shops"
                    monthlyPrice={39}
                    annualPrice={390}
                    cadenceSuffix={cadenceSuffix}
                    billingCycle={billingCycle}
                    featured
                    bullets={[
                      'Everything in Home',
                      'Commercial-use license',
                      'Priority support',
                      'Early access to new features',
                    ]}
                    isCurrent={isCurrent('pro-platform')}
                    onCheckout={handleCheckout}
                    isPending={checkout.isPending}
                    pendingProduct={pendingProduct}
                  />
                  <PlanCard
                    product="studio-team"
                    name="Busswerks Studio Team"
                    tagline="For teams — SSO, pooled billing, shared layouts"
                    monthlyPrice={49}
                    annualPrice={490}
                    cadenceSuffix={seatSuffix}
                    billingCycle={billingCycle}
                    bullets={[
                      'Everything in Pro',
                      'SSO via WorkOS',
                      'Pooled team billing',
                      'Unlimited collaborators per layout',
                      'Studio onboarding + priority support',
                    ]}
                    isCurrent={isCurrent('studio-team')}
                    onCheckout={handleCheckout}
                    isPending={checkout.isPending}
                    pendingProduct={pendingProduct}
                    seats={seats}
                    onSeatsChange={setSeats}
                  />
                </div>

                {error && (
                  <div className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
                    {error}
                  </div>
                )}

                <div className="rounded-lg border border-accent/30 bg-accent-muted/40 px-3 py-2 text-center">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-accent">
                    Founding member price lock
                  </p>
                  <p className="text-[11px] text-content-secondary leading-relaxed mt-0.5">
                    Subscribe before a new app ships and this price stays put
                    for the lifetime of your subscription.
                  </p>
                </div>
                <p className="text-[10px] text-content-tertiary text-center">
                  You&apos;ll be redirected to Stripe Checkout to complete
                  payment. Cancel or change plan anytime from the billing
                  portal.
                </p>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
